/**
 * Statistics API Service
 * Handles all statistics-related API calls
 */

import apiClient from './apiClient';

export interface PlayerGlobalStats {
  totalRoomsPlayed: number;
  totalGamesPlayed: number;
  totalGamesWon: number;
  overallWinRate: number;
  totalScore: number;
  averageScore: number;
  bestRoomScore: number;
}

export interface PlayerRoomStats {
  playerId: string;
  playerName: string;
  cumulativeScore: number;
  gamesWon: number;
  gamesPlayed: number;
  winRate: number;
  averageScore: number;
  bestGameScore: number;
  lastPlayedAt: string;
  rank?: number;
}

export interface RoomLeaderboard {
  roomId: string;
  roomName?: string;
  gamesPlayed: number;
  lastActivityAt: string;
  scores: PlayerRoomStats[];
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt?: Date;
  progress?: number;
  target?: number;
}

class StatisticsApi {
  /**
   * Get player's global statistics across all rooms
   */
  async getPlayerGlobalStats(userId: string): Promise<PlayerGlobalStats> {
    try {
      console.log('[statisticsApi] getPlayerGlobalStats called for userId:', userId);
      const url = `/players/${userId}/stats`;
      console.log('[statisticsApi] Making GET request to:', url);
      
      // Using apiClient - 401 refresh handled automatically
      const response = await apiClient.get(url);
      
      console.log('[statisticsApi] Response received:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('[statisticsApi] Error fetching global stats:', error);
      throw error;
    }
  }  /**
   * Get leaderboard for a specific room
   */
  async getRoomLeaderboard(roomId: string): Promise<RoomLeaderboard> {
    try {
      // Using apiClient - 401 refresh handled automatically
  // Backend route: GET /api/rooms/:roomId/scores
  const response = await apiClient.get(`/rooms/${roomId}/scores`);
      const data = response.data.data;
      
      // Add rank to each player
      const scoresWithRank = data.scores
        .sort((a: PlayerRoomStats, b: PlayerRoomStats) => b.cumulativeScore - a.cumulativeScore)
        .map((score: PlayerRoomStats, index: number) => ({
          ...score,
          rank: index + 1
        }));
      
      return {
        ...data,
        scores: scoresWithRank
      };
    } catch (error) {
      console.error('Error fetching room leaderboard:', error);
      throw error;
    }
  }

  /**
   * Get player's room-specific statistics
   */
  async getPlayerRoomStats(roomId: string, userId: string): Promise<PlayerRoomStats | null> {
    try {
      // Using apiClient - 401 refresh handled automatically
      const response = await apiClient.get(`/room-scores/room/${roomId}/player/${userId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching player room stats:', error);
      return null;
    }
  }

  /**
   * Calculate achievements based on player stats
   */
  calculateAchievements(globalStats: PlayerGlobalStats): Achievement[] {
    const achievements: Achievement[] = [];

    // Games Played Milestones
    if (globalStats.totalGamesPlayed >= 1) {
      achievements.push({
        id: 'first_game',
        name: 'First Steps',
        description: 'Played your first game',
        icon: '🎮',
        unlockedAt: new Date()
      });
    }
    
    if (globalStats.totalGamesPlayed >= 10) {
      achievements.push({
        id: 'regular_player',
        name: 'Regular Player',
        description: 'Played 10 games',
        icon: '🎯',
        unlockedAt: new Date()
      });
    }
    
    if (globalStats.totalGamesPlayed >= 50) {
      achievements.push({
        id: 'veteran',
        name: 'Veteran',
        description: 'Played 50 games',
        icon: '⭐',
        unlockedAt: new Date()
      });
    }
    
    if (globalStats.totalGamesPlayed >= 100) {
      achievements.push({
        id: 'master',
        name: 'Master Player',
        description: 'Played 100 games',
        icon: '👑',
        unlockedAt: new Date()
      });
    }

    // Win Rate Achievements
    if (globalStats.totalGamesPlayed >= 10 && globalStats.overallWinRate >= 50) {
      achievements.push({
        id: 'winning_streak',
        name: 'Winning Streak',
        description: 'Maintain 50%+ win rate',
        icon: '🔥',
        unlockedAt: new Date()
      });
    }
    
    if (globalStats.totalGamesPlayed >= 20 && globalStats.overallWinRate >= 70) {
      achievements.push({
        id: 'champion',
        name: 'Champion',
        description: 'Maintain 70%+ win rate',
        icon: '🏆',
        unlockedAt: new Date()
      });
    }

    // Score Achievements
    if (globalStats.bestRoomScore >= 500) {
      achievements.push({
        id: 'high_scorer',
        name: 'High Scorer',
        description: 'Score 500+ in a single game',
        icon: '💯',
        unlockedAt: new Date()
      });
    }
    
    if (globalStats.totalScore >= 5000) {
      achievements.push({
        id: 'point_collector',
        name: 'Point Collector',
        description: 'Accumulate 5000+ total points',
        icon: '💎',
        unlockedAt: new Date()
      });
    }

    // Room Participation
    if (globalStats.totalRoomsPlayed >= 3) {
      achievements.push({
        id: 'explorer',
        name: 'Room Explorer',
        description: 'Played in 3+ different rooms',
        icon: '🗺️',
        unlockedAt: new Date()
      });
    }

    return achievements;
  }

  /**
   * Get player level based on experience (games played and score)
   */
  calculatePlayerLevel(globalStats: PlayerGlobalStats): { level: number; xp: number; nextLevelXp: number; progress: number } {
    // XP = (games played * 100) + (total score / 10)
    const xp = (globalStats.totalGamesPlayed * 100) + Math.floor(globalStats.totalScore / 10);
    
    // Level progression: Level = floor(sqrt(XP / 100))
    const level = Math.floor(Math.sqrt(xp / 100)) + 1;
    
    // XP needed for current level
    const currentLevelXp = Math.pow(level - 1, 2) * 100;
    
    // XP needed for next level
    const nextLevelXp = Math.pow(level, 2) * 100;
    
    // Progress towards next level
    const progress = ((xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100;
    
    return {
      level,
      xp,
      nextLevelXp,
      progress: Math.min(100, Math.max(0, progress))
    };
  }

  /**
   * Get rank title based on win rate and level
   */
  getRankTitle(_winRate: number, level: number): string {
    if (level >= 20) return '🌟 Legendary';
    if (level >= 15) return '💎 Master';
    if (level >= 10) return '🏆 Expert';
    if (level >= 7) return '⭐ Advanced';
    if (level >= 5) return '🎯 Skilled';
    if (level >= 3) return '🎮 Regular';
    return '🌱 Beginner';
  }
}

export const statisticsApi = new StatisticsApi();
